import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

let isRefreshing = false;
let failedQueue = [];
let isWaitingForReconnect = false;
let reconnectQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

const processReconnectQueue = (error, token = null) => {
  reconnectQueue.forEach(({ resolve, reject, originalRequest }) => {
    if (error) {
      reject(error);
    } else {
      originalRequest.headers['Authorization'] = `Bearer ${token}`;
      resolve(axiosInstance(originalRequest));
    }
  });
  reconnectQueue = [];
  isWaitingForReconnect = false;
};

window.addEventListener('reconnect-success', () => {
  const accessToken = localStorage.getItem('accessToken');
  processReconnectQueue(null, accessToken);
});

window.addEventListener('reconnect-logout', () => {
  const error = new Error('Session expirée - déconnexion');
  error.isLogout = true;
  processReconnectQueue(error, null);
});

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

axiosInstance.interceptors.request.use(
  config => {
    const accessToken = localStorage.getItem('accessToken');
    config.headers['Authorization'] = `Bearer ${accessToken}`;
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  response => {
    return response;
  },
  async error => {
    const originalRequest = error.config;

    if (!error.response) {
      console.error('Erreur réseau ou autre problème non HTTP', error);
      return Promise.reject(error);
    }

    // Si la réponse est un Blob (ex: requête PDF), parser le JSON
    let data = error.response.data;
    if (data instanceof Blob) {
      try {
        const text = await data.text();
        data = JSON.parse(text);
      } catch {
        data = {};
      }
    }

    const isAuthRoute = originalRequest.url?.includes('/token/');
    const isTokenExpired =
      data?.code === 'token_not_valid' || data?.error?.message?.code === 'token_not_valid';

    if (
      error.response.status === 401 &&
      !originalRequest._retry &&
      !isAuthRoute &&
      isTokenExpired
    ) {
      // Si on attend déjà une reconnexion, mettre la requête en file d'attente
      if (isWaitingForReconnect) {
        return new Promise((resolve, reject) => {
          reconnectQueue.push({ resolve, reject, originalRequest });
        });
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return axiosInstance(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const accessToken = await refreshAccessToken();
        processQueue(null, accessToken);
        originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        processQueue(null, null);
        console.error('Erreur lors du rafraîchissement du token:', refreshError);

        // Passer en mode attente de reconnexion
        isWaitingForReconnect = true;

        // Afficher la modale de reconnexion
        window.dispatchEvent(new CustomEvent('session-expired'));

        // Mettre la requête originale en file d'attente de reconnexion
        return new Promise((resolve, reject) => {
          reconnectQueue.push({ resolve, reject, originalRequest });
        });
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem('refreshToken');

  if (!refreshToken) {
    throw new Error('Aucun refresh token disponible');
  }

  try {
    const response = await axios.post(`${API_BASE_URL}token/refresh/`, {
      refresh: refreshToken,
    });

    if (response.data && response.data.access) {
      localStorage.setItem('accessToken', response.data.access);
      if (response.data.refresh) {
        localStorage.setItem('refreshToken', response.data.refresh);
      }
      return response.data.access;
    } else {
      throw new Error('Réponse invalide du serveur lors du refresh');
    }
  } catch (error) {
    console.error("Erreur lors du rafraîchissement du token d'accès:", error);

    if (error.response && error.response.status === 401) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    }

    throw error;
  }
};

export default axiosInstance;
